import React, { useState, useEffect } from 'react';
import OpenAIModelSelector from '../components/OpenAIModelSelector';
import emailAnalyticsService from '../services/emailAnalyticsService';
import whatsappAnalyticsService from '../services/whatsappAnalyticsService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Api_Stadistics = () => {
  const [currentEmailStats, setCurrentEmailStats] = useState(null);
  const [monthlyEmailStats, setMonthlyEmailStats] = useState(null);
  const [loadingEmailStats, setLoadingEmailStats] = useState(true);

  const [currentWhatsAppStats, setCurrentWhatsAppStats] = useState(null);
  const [monthlyWhatsAppStats, setMonthlyWhatsAppStats] = useState(null);
  const [loadingWhatsAppStats, setLoadingWhatsAppStats] = useState(true);

  // Cargar estadísticas de emails al montar el componente
  useEffect(() => {
    const fetchEmailStats = async () => {
      try {
        setLoadingEmailStats(true);
        const [current, monthly] = await Promise.all([
          emailAnalyticsService.getCurrentStats(),
          emailAnalyticsService.getMonthlyStats()
        ]);
        setCurrentEmailStats(current);
        setMonthlyEmailStats(monthly);
      } catch (error) {
        console.error('Error al cargar estadísticas de emails:', error);
        toast.error('No se pudieron cargar las estadísticas de emails');
      } finally {
        setLoadingEmailStats(false);
      }
    };

    fetchEmailStats();
  }, []);

  // Cargar estadísticas de WhatsApp al montar el componente
  useEffect(() => {
    const fetchWhatsAppStats = async () => {
      try {
        setLoadingWhatsAppStats(true);
        const [current, monthly] = await Promise.all([
          whatsappAnalyticsService.getCurrentStats(),
          whatsappAnalyticsService.getMonthlyStats()
        ]);
        setCurrentWhatsAppStats(current);
        setMonthlyWhatsAppStats(monthly);
      } catch (error) {
        console.error('Error al cargar estadísticas de WhatsApp:', error);
        toast.error('No se pudieron cargar las estadísticas de WhatsApp');
      } finally {
        setLoadingWhatsAppStats(false);
      }
    };

    fetchWhatsAppStats();
  }, []);

  // Función para formatear números
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return new Intl.NumberFormat('es-CO').format(num);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Estadísticas API</h2>
      
      {/* Selector de modelo OpenAI */}
      <div className="mb-6">
        <OpenAIModelSelector />
      </div>

      {/* Estadísticas de Emails */}
      <div className="mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-700">Estadísticas de Emails (EmailerSend)</h3>
          </div>

          {loadingEmailStats ? (
            <div className="flex justify-center items-center py-8">
              <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Emails enviados hoy */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Emails Enviados Hoy</p>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-blue-700">
                  {currentEmailStats ? formatNumber(currentEmailStats.totalSent) : '0'}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {currentEmailStats?.period || 'Diario'}
                </p>
              </div>

              {/* Emails enviados este mes */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Emails Enviados Este Mes</p>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-green-700">
                  {monthlyEmailStats ? formatNumber(monthlyEmailStats.totalSent) : '0'}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {monthlyEmailStats?.period || 'Mensual'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Estadísticas de WhatsApp */}
      <div className="mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-700">Estadísticas de WhatsApp</h3>
          </div>

          {loadingWhatsAppStats ? (
            <div className="flex justify-center items-center py-8">
              <svg className="animate-spin h-8 w-8 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Estadísticas de Hoy */}
              <div>
                <h4 className="text-sm font-semibold text-gray-600 mb-3">Hoy</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Mensajes Enviados</p>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-green-700">
                      {currentWhatsAppStats ? formatNumber(currentWhatsAppStats.totalMessagesSent) : '0'}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {currentWhatsAppStats?.period || 'Diario'}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Números Únicos</p>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-blue-700">
                      {currentWhatsAppStats ? formatNumber(currentWhatsAppStats.uniqueRecipientsCount) : '0'}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Destinatarios únicos
                    </p>
                  </div>
                </div>
              </div>

              {/* Estadísticas del Mes */}
              <div>
                <h4 className="text-sm font-semibold text-gray-600 mb-3">Este Mes</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Mensajes Enviados</p>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-purple-700">
                      {monthlyWhatsAppStats ? formatNumber(monthlyWhatsAppStats.totalMessagesSent) : '0'}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {monthlyWhatsAppStats?.period || 'Mensual'}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Números Únicos</p>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-orange-700">
                      {monthlyWhatsAppStats ? formatNumber(monthlyWhatsAppStats.uniqueRecipientsCount) : '0'}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Destinatarios únicos
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Api_Stadistics;
